'use client';

import { Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
    Search,
    Filter,
    Cpu,
    MessageSquare,
    ClipboardList,
    ArrowRight,
    CheckCircle2,
    Target,
    Zap,
    AlertCircle,
    Compass,
    Globe
} from 'lucide-react';
import { GlobalBrandedLoader } from '@/doi-tac-module/components/global-branded-loader';
import { AgencyReferralCard } from '@/doi-tac-module/components/quy-trinh/agency-referral-card';
import React, { useState, useEffect } from 'react';
import { validateAgencyCode } from '@/doi-tac-module/actions/agency-actions';
import { agencyTranslations as at } from '@/doi-tac-module/constants/agency-translations';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { cn } from '@/doi-tac-module/lib/utils';

const jobSearchRoadmapTranslations: any = {
    hero: {
        badge: { vi: "Tìm kiếm Việc làm Thông minh", en: "Smart Job Search" },
        titlePrefix: { vi: "Lộ trình Tìm kiếm", en: "Search Roadmap" },
        titleSuffix: { vi: "Việc làm Tokutei", en: "Tokutei Jobs" },
        description: { 
            vi: "Tiếp cận hàng nghìn đơn hàng Tokutei minh bạch, đa dạng ngành nghề và khu vực tại Nhật Bản với sự hỗ trợ của công nghệ AI.",
            en: "Access thousands of transparent Tokutei job listings across various industries and regions in Japan with the help of AI technology."
        },
        descriptionDesktop: {
            vi: "Hệ thống HelloJob giúp ứng viên Tokutei dễ dàng tìm thấy công việc mơ ước thông qua bộ lọc thông minh và gợi ý AI chính xác. Chúng tôi cam kết mang đến những thông tin đơn hàng minh bạch nhất, giúp bạn an tâm lựa chọn và phát triển sự nghiệp lâu dài tại xứ sở mặt trời mọc.",
            en: "The HelloJob system helps Tokutei candidates easily find their dream jobs through smart filters and accurate AI suggestions. We are committed to providing the most transparent job information, helping you choose with confidence and build a long-term career in the Land of the Rising Sun."
        },
        ctaRegister: { vi: "Khám phá việc làm", en: "Explore Jobs" },
        ctaProcess: { vi: "Tìm hiểu lộ trình", en: "Learn roadmap" }
    },
    painPoints: {
        item1: {
            title: { vi: "Thông tin mập mờ", en: "Vague Information" },
            description: { 
                vi: "Nhiều đơn hàng không ghi rõ mức lương, chế độ hoặc yêu cầu cụ thể, gây khó khăn cho ứng viên.",
                en: "Many job listings do not clearly state salary, benefits, or specific requirements, causing difficulties for candidates."
            }
        },
        item2: {
            title: { vi: "Khó tìm việc đúng ngành", en: "Difficult to Find Right Industry" },
            description: { 
                vi: "Mất nhiều thời gian để sàng lọc giữa hàng trăm thông tin không phù hợp với ngành nghề Tokutei của bạn.",
                en: "Spending too much time filtering through hundreds of irrelevant information not matching your Tokutei industry."
            }
        },
        item3: {
            title: { vi: "Thiếu công cụ hỗ trợ", en: "Lack of Support Tools" },
            description: { 
                vi: "Không có hệ thống tự động gợi ý các công việc dựa trên năng lực và hồ sơ cá nhân.",
                en: "No automated system to suggest jobs based on personal ability and profile."
            }
        }
    },
    solutions: {
        title: { vi: "Giải pháp từ HelloJob", en: "Solutions from HelloJob" },
        subtitle: { vi: "Nhanh Chóng – Chính Xác – Minh Bạch", en: "Fast – Accurate – Transparent" },
        items: [
            { vi: "Bộ lọc đơn hàng chi tiết theo tỉnh thành, ngành nghề và mức lương mong muốn.", en: "Detailed job filters by province, industry, and desired salary." },
            { vi: "AI tự động phân tích hồ sơ và gợi ý những công việc phù hợp nhất với bạn.", en: "AI automatically analyzes profiles and suggests the most suitable jobs for you." },
            { vi: "Thông tin nhà tuyển dụng và chế độ đãi ngộ được công khai minh bạch 100%.", en: "Employer information and benefits are 100% publicly transparent." },
            { vi: "Hệ thống tin nhắn trực tiếp giúp bạn kết nối nhanh chóng với nhà tuyển dụng.", en: "Direct messaging system helps you connect quickly with employers." }
        ]
    },
    process: {
        title: { vi: "Quy trình Tìm việc 5 Bước", en: "5-Step Job Search Process" },
        description: { 
            vi: "Lộ trình tối ưu giúp bạn tìm được công việc Tokutei ưng ý nhất trong thời gian ngắn nhất.",
            en: "An optimized roadmap helping you find the most satisfactory Tokutei job in the shortest time."
        },
        ctaAction: { vi: "Xem danh sách việc làm", en: "View Job List" },
        steps: [
            {
                title: { vi: "1. Thiết lập tiêu chí", en: "1. Set Criteria" },
                stageLabel: { vi: "Bắt đầu", en: "Start" },
                description: { 
                    vi: "Xác định rõ ngành nghề Tokutei bạn đang có chứng chỉ, khu vực muốn làm việc và mức lương kỳ vọng.",
                    en: "Clearly define the Tokutei industry you have a certificate in, the area you want to work, and expected salary."
                }
            },
            {
                title: { vi: "2. Khám phá đơn hàng", en: "2. Explore Listings" },
                stageLabel: { vi: "Tìm kiếm", en: "Search" },
                description: { 
                    vi: "Sử dụng bộ lọc thông minh để tìm kiếm trong kho dữ liệu hàng nghìn đơn hàng Tokutei mới nhất mỗi ngày.",
                    en: "Use smart filters to search through a database of thousands of the latest Tokutei job listings daily."
                }
            },
            {
                title: { vi: "3. AI Gợi ý việc làm", en: "3. AI Job Suggestion" },
                stageLabel: { vi: "Gợi ý AI", en: "AI Suggest" },
                description: { 
                    vi: "Hệ thống AI sẽ so khớp hồ sơ của bạn với các yêu cầu của nhà tuyển dụng để đưa ra những gợi ý tối ưu nhất.",
                    en: "The AI system will match your profile with employer requirements to provide the best suggestions."
                }
            },
            {
                title: { vi: "4. Ứng tuyển & Kết nối", en: "4. Apply & Connect" },
                stageLabel: { vi: "Tương tác", en: "Interact" },
                description: { 
                    vi: "Gửi hồ sơ ứng tuyển trực tiếp và trao đổi thêm với nhà tuyển dụng qua hệ thống chat tích hợp.",
                    en: "Submit your application directly and discuss further with employers through the integrated chat system."
                }
            },
            {
                title: { vi: "5. Theo dõi & Phỏng vấn", en: "5. Track & Interview" },
                stageLabel: { vi: "Thành công", en: "Success" },
                description: { 
                    vi: "Quản lý trạng thái các đơn đã ứng tuyển và chuẩn bị sẵn sàng cho các buổi phỏng vấn trực tiếp.",
                    en: "Manage the status of applied jobs and get ready for direct interviews."
                }
            }
        ]
    },
    finalCta: {
        title: { vi: "Tìm thấy công việc mơ ước của bạn ngay hôm nay", en: "Find Your Dream Job Today" },
        description: { 
            vi: "Hàng nghìn ứng viên đã thành công cùng HelloJob. Đã đến lượt bạn!",
            en: "Thousands of candidates have succeeded with HelloJob. It's your turn!"
        },
        trustLabel: { vi: "Kết nối trực tiếp – Không qua trung gian", en: "Direct connection – No middleman" }
    }
};

const stepsData = [
    {
        id: 1,
        icon: Target,
        color: "bg-[#F2B92A]/10 text-[#F2B92A]",
        image: "/img/PHONGVAN/phongvan01.jpg",
        dataAiHint: "setting job search goals"
    },
    {
        id: 2,
        icon: Search,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan02.jpg",
        dataAiHint: "searching jobs on a tablet"
    },
    {
        id: 3,
        icon: Cpu,
        color: "bg-[#AFC536]/10 text-[#AFC536]",
        image: "/img/PHONGVAN/phongvan03.jpg",
        dataAiHint: "AI matching profile with jobs"
    },
    {
        id: 4,
        icon: MessageSquare,
        color: "bg-[#0D8DC8]/10 text-[#0D8DC8]",
        image: "/img/PHONGVAN/phongvan04.jpg",
        dataAiHint: "chatting with employer"
    },
    {
        id: 5,
        icon: ClipboardList,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan05.jpg",
        dataAiHint: "tracking application status"
    }
];

const painPointsData = [
    { icon: AlertCircle, key: 'item1' },
    { icon: Compass, key: 'item2' },
    { icon: Zap, key: 'item3' }
];

function JobSearchRoadmapContent() {
    const { language } = useLanguage();
    const router = useRouter();
    const searchParams = useSearchParams();
    const refId = searchParams.get('ref');
    
    const { user, isLoggedIn } = useAuth();
    const t = jobSearchRoadmapTranslations;

    const [isValidatingRef, setIsValidatingRef] = useState(!!refId);
    const [showInvalidRefPopup, setShowInvalidRefPopup] = useState(false);

    // Redirect target based on login status
    const mainBtnLink = isLoggedIn ? "/tim-viec-lam" : (refId ? `/xac-thuc?ref=${refId}&redirect=/tim-viec-lam` : "/xac-thuc?redirect=/tim-viec-lam");
    const mainBtnText = isLoggedIn ? "Xem việc làm ngay" : t.hero.ctaRegister[language];

    useEffect(() => {
        const checkRef = async () => {
            if (!refId) return;
            
            setIsValidatingRef(true);
            try {
                const res = await validateAgencyCode(refId);
                if (!res.success) {
                    setShowInvalidRefPopup(true);
                }
            } catch (err) {
                console.error("Failed to validate ref code on roadmap:", err);
                setShowInvalidRefPopup(true);
            } finally {
                setIsValidatingRef(false);
            }
        };
        
        checkRef();
    }, [refId]);

    return (
        <div className="flex flex-col w-full min-h-screen bg-slate-50/50">
            {/* Hero Section */}
            <section className="relative w-full pt-20 md:pt-28 pb-20 bg-[#F2B92A] bg-gradient-to-br from-[#F2B92A] to-[#D9A31E] overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,rgba(255,255,255,0),rgba(255,255,255,0.5))]" />
                <div className="container relative mx-auto px-4 md:px-6 text-center text-white">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 mb-8 animate-in fade-in slide-in-from-top-10">
                        <Target className="h-4 w-4" />
                        <span className="text-sm font-bold">{t.hero.badge[language]}</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-headline font-bold mb-8 leading-snug md:leading-tight animate-in fade-in slide-in-from-top-10 duration-500">
                        {t.hero.titlePrefix[language]} <br /> <span className="text-slate-900">{t.hero.titleSuffix[language]}</span>
                    </h1>
                    <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed font-medium animate-in fade-in slide-in-from-top-10 duration-700">
                        {t.hero.descriptionDesktop[language]}
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 animate-in fade-in slide-in-from-top-10 duration-1000">
                        <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-12 px-10 text-lg rounded-xl shadow-lg transition-all hover:scale-105" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                        <Button variant="outline" size="lg" className="bg-white/10 border-white/40 text-white hover:bg-white/20 backdrop-blur-md h-12 px-10 text-lg rounded-xl font-bold transition-all" asChild>
                            <Link href="#process">{t.hero.ctaProcess[language]}</Link>
                        </Button>
                    </div>
                </div>
                
                {/* Decoration */}
                <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-64 h-64 bg-black/5 rounded-full blur-2xl" />
            </section>

            {/* Pain Points Section */}
            <section className="w-full py-24 -mt-12 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {painPointsData.map((point, index) => {
                            const content = (t.painPoints as any)[point.key];
                            return (
                                <Card key={index} className="border-none shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden group hover:scale-[1.02] transition-all duration-500 bg-white">
                                    <CardContent className="p-10">
                                        <div className="h-14 w-14 rounded-2xl bg-[#F2B92A]/10 text-[#F2B92A] flex items-center justify-center mb-8 group-hover:bg-[#F2B92A] group-hover:text-white transition-all duration-500 transform group-hover:rotate-6">
                                            <point.icon className="h-7 w-7" />
                                        </div>
                                        <h3 className="text-2xl font-headline font-bold mb-4 text-slate-800">
                                            {content.title[language]}
                                        </h3>
                                        <p className="text-slate-500 leading-relaxed font-medium">
                                            {content.description[language]}
                                        </p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Solutions Section */}
            <section className="w-full py-24 bg-white">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col-reverse lg:flex-row items-center gap-16 lg:gap-24">
                        <div className="lg:w-1/2 w-full">
                            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl bg-slate-100 group">
                                <Image
                                    src="/img/BANGLADESH/TRANGVIETNAM/lotrinh0.jpg"
                                    alt="Job Search Solutions"
                                    fill
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </div>
                        </div>
                        <div className="lg:w-1/2">
                            <div className="space-y-4 mb-10">
                                <Badge className="bg-[#19A6DF]/10 text-[#19A6DF] hover:bg-[#19A6DF]/20 border-none px-4 py-1.5 rounded-full text-sm font-bold">
                                    {t.solutions.subtitle[language]}
                                </Badge>
                                <h2 className="text-4xl md:text-5xl font-headline font-bold text-slate-900 leading-tight">
                                    {t.solutions.title[language]}
                                </h2>
                            </div>
                            <div className="space-y-6">
                                {t.solutions.items.map((item: any, i: number) => (
                                    <div key={i} className="flex items-start gap-5 p-4 rounded-2xl hover:bg-slate-50 transition-colors group">
                                        <div className="mt-1 h-8 w-8 rounded-full bg-green-100 text-green-600 flex flex-shrink-0 items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-all duration-300">
                                            <CheckCircle2 className="h-5 w-5" />
                                        </div>
                                        <p className="text-lg text-slate-700 font-medium">{item[language]}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-12">
                                <Button size="lg" className="bg-[#19A6DF] hover:bg-[#19A6DF]/90 text-white font-bold h-12 px-10 rounded-xl shadow-xl shadow-[#19A6DF]/20 transition-all hover:translate-x-2" asChild>
                                    <Link href={mainBtnLink}>
                                        {mainBtnText}
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Steps Section */}
            <section id="process" className="w-full py-32 bg-slate-50/50">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-24">
                        <h2 className="text-3xl md:text-4xl font-headline font-bold mb-6 text-slate-900">
                            {t.process.title[language]}
                        </h2>
                        <div className="h-1.5 w-24 bg-[#F2B92A] mx-auto rounded-full mb-8" />
                        <p className="text-xl text-slate-500 font-medium">
                            {t.process.description[language]}
                        </p>
                    </div>

                    <div className="space-y-32">
                        {stepsData.map((step, index) => {
                            const stepContent = t.process.steps[index];
                            return (
                                <div key={step.id} className={`flex flex-col-reverse lg:flex-row items-center gap-16 lg:gap-24 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                                    <div className="lg:w-1/2 w-full">
                                        <div className="relative group">
                                            <div className={`absolute -inset-4 rounded-3xl ${step.color} blur-3xl opacity-30 group-hover:opacity-50 transition-opacity`} />
                                            <div className="relative aspect-[3/2] rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-white">
                                                <Image
                                                    src={step.image}
                                                    alt={stepContent.title[language]}
                                                    fill
                                                    sizes="(max-width: 768px) 100vw, 50vw"
                                                    priority={index === 0}
                                                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                                                    data-ai-hint={step.dataAiHint}
                                                />
                                            </div>
                                            {/* Number indicator */}
                                            <div className="absolute -top-6 -left-6 w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-xl font-bold shadow-xl">
                                                {step.id}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="lg:w-1/2">
                                        <div className={`inline-flex items-center gap-2 mb-6 px-5 py-2 rounded-xl text-sm font-bold tracking-widest ${step.color}`}>
                                            <step.icon className="h-5 w-5" />
                                            {stepContent.stageLabel[language]}
                                        </div>
                                        <h3 className="text-2xl md:text-3xl font-headline font-bold mb-6 text-slate-900 leading-tight">
                                            {stepContent.title[language]}
                                        </h3>
                                        <p className="text-xl text-slate-600 leading-relaxed mb-10 font-medium">
                                            {stepContent.description[language]}
                                        </p>
                                        <Button size="lg" className={cn(
                                            "font-bold h-12 px-10 rounded-xl shadow-lg transition-all group",
                                            index % 2 === 0 ? "bg-slate-900 hover:bg-slate-800 text-white" : "bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white"
                                        )} asChild>
                                            <Link href={mainBtnLink}>
                                                {language === 'vi' ? "Bắt đầu ngay" : "Start now"}
                                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="w-full py-32 bg-slate-900 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#F2B92A]/10 rounded-full blur-[120px] -mr-64 -mt-64" />
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#19A6DF]/10 rounded-full blur-[120px] -ml-64 -mb-64" />

                <div className="container relative mx-auto px-4 md:px-6 text-center z-10">
                    <h2 className="text-3xl md:text-4xl font-headline font-bold mb-10 tracking-tight">
                        {t.finalCta.title[language]}
                    </h2>
                    <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto mb-16 font-medium">
                        {t.finalCta.description[language]}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-6">
                        <Button size="lg" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-bold h-12 px-12 text-xl rounded-xl shadow-xl shadow-[#F2B92A]/20 transition-all hover:scale-105" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                    </div>

                    <div className="mt-20 flex flex-wrap items-center justify-center gap-12 opacity-40 grayscale">
                        <div className="flex items-center gap-3">
                            <Globe className="h-8 w-8" />
                            <span className="text-sm font-bold tracking-[0.3em]">HelloJob Platform</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Zap className="h-8 w-8" />
                            <span className="text-sm font-bold tracking-[0.3em]">{t.finalCta.trustLabel[language]}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Referral Code Validation Popups */}
            <AlertDialog open={showInvalidRefPopup}>
                <AlertDialogContent className="max-w-md rounded-[2.5rem] border-none p-10">
                    <AlertDialogHeader>
                        <div className="mx-auto bg-red-100 rounded-3xl p-4 w-fit mb-6">
                            <AlertCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <AlertDialogTitle className="text-3xl font-bold text-center font-headline text-slate-900">
                            {at.sso.invalidRef.title[language]}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-center text-slate-600 text-lg font-medium mt-4">
                            {at.sso.invalidRef.desc[language]}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-10">
                        <AlertDialogAction 
                            onClick={() => router.push('/quy-trinh')}
                            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-lg font-bold shadow-xl transition-all"
                        >
                            {at.sso.invalidRef.button[language]}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {isValidatingRef && <GlobalBrandedLoader forceShow={true} />}
            
            {/* Agency Referral Card if in context */}
            <AgencyReferralCard />
        </div>
    );
}

export default function JobSearchRoadmapPage() {
    return (
        <Suspense fallback={<GlobalBrandedLoader forceShow={true} />}>
            <JobSearchRoadmapContent />
        </Suspense>
    );
}
